import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const NAV_ITEMS = [
  { key: "profile", label: "Profile & Account", path: "/settings/profile" },
  { key: "security", label: "Security & Authentication", path: "/settings/security-authentication" },
  { key: "identity", label: "Digital Identity", path: "/settings/digital-identity" },
  { key: "certs", label: "Certificates", path: "/settings/certificates" },
  { key: "pqc", label: "Post-Quantum (PQC)", path: "/settings/post-quantum" },
  { key: "blockchain", label: "Blockchain & Anchoring", path: "/settings/blockchain-anchoring" },
  { key: "notifications", label: "Notifications", path: "/settings/my-notifications" },
  { key: "users", label: "Users & Roles", path: "/users-control" },
  { key: "orgs", label: "Organizations", path: "/settings/organizations" },
  { key: "integrations", label: "Integrations", path: "/settings/integrations" },
  { key: "audit", label: "Audit & Logs", path: "/settings/audit-logs" },
  { key: "backup", label: "Backup & Recovery", path: "/settings/backup-recovery" },
  { key: "system", label: "System Preferences", path: "/settings/system-preferences" },
];

const PROFILE_FIELDS = [
  { label: "Full Name", value: "Demo Admin" },
  { label: "Email Address", value: "admin@intrust.com" },
  { label: "Phone Number", value: "+91 98765 43210" },
  { label: "Job Title", value: "Administrator" },
  { label: "Department", value: "IT Security" },
  { label: "Organization", value: "Intrust Technologies" },
  { label: "Account Type", value: "Enterprise" },
  { label: "Role", value: "Super Admin" },
  { label: "Member Since", value: "Jan 15, 2026" },
];

function Icon({ children, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}

export default function SettingsProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [saved, setSaved] = useState(false);
  const [language, setLanguage] = useState("English (US)");
  const [timezone, setTimezone] = useState("(UTC +05:30) Asia/Kolkata");
  const [timeFormat, setTimeFormat] = useState("10:45 AM (12-hour)");

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  return (
    <div style={styles.wrapper}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { font-family: 'Inter', sans-serif; box-sizing: border-box; }
        select:focus, input:focus { outline: 2px solid #efecfc; border-color: #6d5bd0; }
        .nav-item:hover { background: #f5f5f8; }
        .btn-outline:hover { background: #f5f5f8; }
        .btn-primary:hover { background: #5f4dc4; }
      `}</style>

      <div style={styles.layout}>
        {/* Settings Sidebar */}
        <div style={styles.sidebar}>
          {NAV_ITEMS.map((item) => (
            <div
              key={item.key}
              className="nav-item"
              onClick={() => navigate(item.path)}
              style={{
                ...styles.navItem,
                ...(location.pathname === item.path ? styles.navItemActive : {}),
              }}
            >
              {item.label}
            </div>
          ))}
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Profile Information */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Profile Information</h2>
            <div style={styles.profileHead}>
              <div style={styles.profileHeadLeft}>
                <div style={styles.avatarLg}>D</div>
                <div>
                  <div style={styles.profileNameRow}>
                    <span style={styles.name}>Demo Admin</span>
                    <span style={{ ...styles.badge, ...styles.badgeGreen }}>Super Admin</span>
                  </div>
                  <div style={styles.profileSub}>
                    admin@intrust.com<br />
                    +91 98765 43210
                  </div>
                </div>
              </div>
              <button className="btn-outline" style={styles.btn}>
                <Icon size={14}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" /></Icon>
                Edit Profile
              </button>
            </div>
            <div style={styles.infoGrid}>
              {PROFILE_FIELDS.map((f) => (
                <div key={f.label}>
                  <div style={styles.fieldLabel}>{f.label}</div>
                  <div style={styles.fieldValue}>{f.value}</div>
                </div>
              ))}
              <div style={styles.statusRow}>
                <div style={styles.fieldLabel}>Account Status</div>
                <span style={{ ...styles.badge, ...styles.badgeGreen, marginTop: 6, display: "inline-block" }}>Active</span>
              </div>
            </div>
          </div>

          {/* Account Preferences */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Account Preferences</h2>
            <div style={styles.formGrid}>
              <div>
                <label style={styles.label}>Language</label>
                <div style={styles.selectWrap}>
                  <select style={styles.select} value={language} onChange={(e) => setLanguage(e.target.value)}>
                    <option>English (US)</option>
                    <option>English (UK)</option>
                    <option>Hindi</option>
                    <option>Tamil</option>
                  </select>
                  <span style={styles.chevron} />
                </div>
              </div>
              <div>
                <label style={styles.label}>Time Zone</label>
                <div style={styles.selectWrap}>
                  <select style={styles.select} value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                    <option>(UTC +05:30) Asia/Kolkata</option>
                    <option>(UTC +00:00) UTC</option>
                    <option>(UTC -05:00) America/New_York</option>
                  </select>
                  <span style={styles.chevron} />
                </div>
              </div>
              <div>
                <label style={styles.label}>Date Format</label>
                <div style={{ position: "relative" }}>
                  <input style={styles.input} readOnly value="June 19, 2026 (MMM DD, YYYY)" />
                  <span style={styles.calendarIcon}>
                    <Icon size={15}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></Icon>
                  </span>
                </div>
              </div>
              <div>
                <label style={styles.label}>Time Format</label>
                <div style={styles.selectWrap}>
                  <select style={styles.select} value={timeFormat} onChange={(e) => setTimeFormat(e.target.value)}>
                    <option>10:45 AM (12-hour)</option>
                    <option>10:45 (24-hour)</option>
                  </select>
                  <span style={styles.chevron} />
                </div>
              </div>
            </div>
            <div style={styles.formActions}>
              <button className="btn-primary" style={styles.btnPrimary} onClick={handleSave}>
                {saved ? "Saved ✓" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const colors = {
  bg: "#f7f8fa",
  card: "#ffffff",
  border: "#e7e9ee",
  textPrimary: "#1a1d29",
  textSecondary: "#6b7280",
  textMuted: "#9ca3af",
  purple: "#6d5bd0",
  purpleLight: "#efecfc",
  greenBg: "#e7f8ef",
  greenText: "#1ea15a",
};

const styles = {
  wrapper: {
    fontFamily: "'Inter', sans-serif",
    background: colors.bg,
    color: colors.textPrimary,
    minHeight: "100vh",
  },
  layout: { display: "grid", gridTemplateColumns: "230px 1fr", gap: 22, alignItems: "start", padding: "4px 0" },
  sidebar: { background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 10, boxShadow: "0 1px 2px rgba(16,24,40,.04)" },
  navItem: { display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 9, fontSize: 13.5, color: colors.textSecondary, cursor: "pointer", marginBottom: 2, fontWeight: 500 },
  navItemActive: { background: colors.purpleLight, color: colors.purple, fontWeight: 600 },
  content: { display: "flex", flexDirection: "column", gap: 22 },
  card: { background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 14, padding: "26px 28px", boxShadow: "0 1px 2px rgba(16,24,40,.04)" },
  cardTitle: { fontSize: 16, fontWeight: 700, margin: "0 0 20px", color: colors.textPrimary },
  profileHead: { display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 20, borderBottom: `1px solid ${colors.border}`, marginBottom: 20, flexWrap: "wrap", gap: 16 },
  profileHeadLeft: { display: "flex", alignItems: "center", gap: 16 },
  avatarLg: { width: 56, height: 56, borderRadius: "50%", background: "#3b2f8f", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 20 },
  profileNameRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 5 },
  name: { fontSize: 16, fontWeight: 700 },
  badge: { fontSize: 11.5, fontWeight: 600, padding: "3px 10px", borderRadius: 20 },
  badgeGreen: { background: colors.greenBg, color: colors.greenText },
  profileSub: { fontSize: 13.5, color: colors.textSecondary, lineHeight: 1.6 },
  btn: { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13.5, fontWeight: 600, padding: "9px 16px", borderRadius: 9, cursor: "pointer", border: `1px solid ${colors.border}`, background: colors.card, color: colors.textPrimary },
  infoGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", rowGap: 22, columnGap: 24 },
  fieldLabel: { fontSize: 12, color: colors.textMuted, marginBottom: 5, fontWeight: 500 },
  fieldValue: { fontSize: 14, fontWeight: 600, color: colors.textPrimary },
  statusRow: { gridColumn: "1 / -1", paddingTop: 20, marginTop: 2, borderTop: `1px solid ${colors.border}` },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 24px" },
  label: { display: "block", fontSize: 12.5, color: colors.textSecondary, marginBottom: 7, fontWeight: 500 },
  selectWrap: { position: "relative" },
  select: { width: "100%", appearance: "none", WebkitAppearance: "none", padding: "10px 36px 10px 14px", border: `1px solid ${colors.border}`, borderRadius: 9, fontSize: 13.5, color: colors.textPrimary, background: colors.card, cursor: "pointer" },
  chevron: { content: "''", position: "absolute", right: 14, top: "50%", width: 9, height: 9, borderRight: `2px solid ${colors.textMuted}`, borderBottom: `2px solid ${colors.textMuted}`, transform: "translateY(-65%) rotate(45deg)", pointerEvents: "none" },
  input: { width: "100%", padding: "10px 36px 10px 14px", border: `1px solid ${colors.border}`, borderRadius: 9, fontSize: 13.5, color: colors.textPrimary, background: colors.card },
  calendarIcon: { position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: colors.textMuted, pointerEvents: "none" },
  formActions: { display: "flex", justifyContent: "flex-end", marginTop: 24 },
  btnPrimary: { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13.5, fontWeight: 600, padding: "9px 20px", borderRadius: 9, cursor: "pointer", border: `1px solid ${colors.purple}`, background: colors.purple, color: "#fff" },
};